import { inject, Service, signal } from '@angular/core';
import { Observable, distinctUntilChanged, map } from 'rxjs';
import { AudioDevicePresence, HeadsetStatus, LiveState } from '@shared/models/live';
import { Medinbox } from './medinbox';
import { WebSocketClient } from '../realtime/web-socket-client';
import { LiveSession } from '../live/live-session';
import { StateEnum } from '@shared/enums/live';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { CurrentUser } from '@core/user/current-user';
import { SHARED_COLORS } from '@shared/constants/live';
import { HeadsetRoutingAction, exclusiveSpeakerActions, nextExclusiveHeadsetIds, toHeadsetAudioStates } from '@shared/utils/live';
import { EquipmentCommand } from '../realtime/equipment-command';
import { LiveCommand } from '../realtime/live-command';
import { InboundWsMessage, isAction } from '@shared/models/websocket';

@Service()
export class HeadsetAudio {
  private readonly liveCommands = inject(LiveCommand);
  private readonly equipmentCommands = inject(EquipmentCommand);
  private readonly medinboxService = inject(Medinbox);
  private readonly webSocketService = inject(WebSocketClient);
  private readonly userService = inject(CurrentUser);
  private readonly liveService = inject(LiveSession);

  private readonly colorMap = [
    { name: 'blue', value: SHARED_COLORS.headsetBlue },
    { name: 'pink', value: SHARED_COLORS.headsetPink },
    { name: 'yellow', value: SHARED_COLORS.headsetYellow },
    { name: 'purple', value: SHARED_COLORS.headsetPurple },
    { name: 'green', value: SHARED_COLORS.headsetGreen },
    { name: 'no-color', value: SHARED_COLORS.headsetNone },
  ];

  private readonly equipmentStatuses = signal<HeadsetStatus[]>([]);
  public readonly equipmentHeadsetStatuses = this.equipmentStatuses.asReadonly();
  private readonly previousEquipmentHeadsetStatuses = signal<HeadsetStatus[]>([]);
  private readonly conferenceStatuses = signal<HeadsetStatus[]>([]);
  public readonly conferenceHeadSetStatuses = this.conferenceStatuses.asReadonly();
  /** Always replaced with the whole broadcast list, never patched, so duplicate or late deliveries are harmless. */
  private readonly devices = signal<AudioDevicePresence[]>([]);
  public readonly audioDevices = this.devices.asReadonly();

  constructor() {
    // Keyed on the session, not the code: a later live can reuse the code and would inherit the old devices.
    this.liveService.currentLive$
      .pipe(
        map((live) => live?.vonageSessionId ?? null),
        distinctUntilChanged(),
      )
      .subscribe(() => this.devices.set([]));

    // Hydrated here, not by the live screen: the init replay lands in the waiting room and would be lost.
    this.webSocketService.message$.subscribe((message) => {
      if (message?.audience === 'LIVE' && isAction(message, WebSocketActionEnum.AUDIO_DEVICES_STATE)) {
        this.devices.set(message.message.params ?? []);
      }
    });

    this.webSocketService.medinboxMessage$.subscribe((message) => {
      if (message?.audience === 'EQUIPMENT' && message.sourceId === this.medinboxService.medinboxId()) {
        // Read on every box message, whatever its action, so params may be another payload (or CAN_RECOVER's null).
        const result = message as InboundWsMessage<WebSocketActionEnum.RESULT>;
        if (
          (isAction(message, WebSocketActionEnum.RESULT) && message.message.params.message.indexOf('HEADSET_STATE') !== -1) ||
          result.message.params?.HeadsetStatus
        ) {
          const statuses = result.message.params.HeadsetStatus as HeadsetStatus[];
          if (statuses.length && this.medinboxService.medinboxConfiguration()) {
            this.setEquipmentHeadsetStatuses(statuses || []);
          }
        }
      }
    });
  }

  setConferenceHeadsetStatuses(statuses: HeadsetStatus[]): void {
    this.conferenceStatuses.set(statuses);
  }

  renameConferenceHeadset(headsetId: number, name: string, shortName: string): void {
    this.conferenceStatuses.update((statuses) => statuses.map((s) => (s.HeadsetId === headsetId ? { ...s, Name: name, ShortName: shortName } : s)));
  }

  /** The box's statuses are stored before the equipment merges them into the conference ones. */
  private setEquipmentHeadsetStatuses(statuses: HeadsetStatus[]): void {
    this.equipmentStatuses.set(statuses);
    if (statuses.length > 0 && this.userService.isEquipment()) {
      this.mergeIntoConferenceStatuses(statuses);
    }
  }

  // Known statuses are patched in place: the previous list and its readers see the change too.
  private mergeIntoConferenceStatuses(hss: HeadsetStatus[]): void {
    const currentConfHeadsetStatuses = this.conferenceStatuses();
    hss.forEach((hs) => {
      const currentIndex = currentConfHeadsetStatuses.findIndex((cchs) => cchs.HeadsetId === hs.HeadsetId);
      if (currentIndex === -1) {
        const color = this.getHeadsetColorFromConfig(hs.HeadsetId);
        const name = this.getHeadsetDefaultNameFromConfig(hs.HeadsetId);
        currentConfHeadsetStatuses.push({
          ...hs,
          Color: this.colorMap.find((cm) => color.indexOf(cm.name) !== -1)?.value ?? SHARED_COLORS.headsetNone,
          Name: name,
          ShortName: this.getShortName(name),
        });
      } else {
        currentConfHeadsetStatuses[currentIndex].IsMuted = hs.IsMuted;
        currentConfHeadsetStatuses[currentIndex].IsOffHooked = hs.IsOffHooked;
        currentConfHeadsetStatuses[currentIndex].IsOnLive = hs.IsOnLive;
        currentConfHeadsetStatuses[currentIndex].RoutedHeadsets = hs.RoutedHeadsets;
      }
    });
    this.conferenceStatuses.set(currentConfHeadsetStatuses.toSorted((a, b) => a.HeadsetId - b.HeadsetId));
    void this.liveCommands.send(WebSocketActionEnum.SET_HEADSET_STATUSES, currentConfHeadsetStatuses, {
      sourceId: this.medinboxService.medinboxId(),
      destinationId: 'WEBSOCKET',
    });
  }

  /** The routing of every headset is recomputed from the exclusive group, its single source of truth. */
  startStopPrivateDiscussion(start: boolean, headsetId: number): void {
    const headsets = this.conferenceStatuses();
    const previousIds = this.exclusiveSpeakerHeadsetIds();
    const nextIds = nextExclusiveHeadsetIds(previousIds, headsetId, start);

    // Captured once at the first activation and shared through the live state, so any client can restore it.
    const isFirstActivation = previousIds.length === 0 && nextIds.length > 0;
    if (isFirstActivation) {
      this.previousEquipmentHeadsetStatuses.set(headsets);
    }
    const snapshot = isFirstActivation ? headsets : this.exclusiveSpeakerSnapshot();

    exclusiveSpeakerActions(toHeadsetAudioStates(headsets), toHeadsetAudioStates(snapshot), nextIds).forEach((action) => this.sendHeadsetRoutingAction(action));

    // headsetId is mandatory: a stop without it makes the websocket clear the whole group.
    void this.liveCommands.send(WebSocketActionEnum.PRIVATE_TALK, { start: start, headsetId: headsetId, previousState: snapshot });
  }

  /** Sends a stop without headset id on purpose: that is what clears the whole group on the websocket. */
  stopAllExclusiveSpeakers(): void {
    if (this.exclusiveSpeakerHeadsetIds().length === 0) {
      return;
    }

    const headsets = this.conferenceStatuses();
    exclusiveSpeakerActions(toHeadsetAudioStates(headsets), toHeadsetAudioStates(this.exclusiveSpeakerSnapshot()), []).forEach((action) =>
      this.sendHeadsetRoutingAction(action),
    );

    void this.liveCommands.send(WebSocketActionEnum.PRIVATE_TALK, { start: false });
  }

  private sendHeadsetRoutingAction(action: HeadsetRoutingAction): void {
    const medinboxId = this.medinboxService.medinboxId() as string;
    void this.equipmentCommands.send(
      action.action,
      {
        MedinboxId: medinboxId,
        HeadsetId: action.headsetId,
        ...(action.otherHeadsetId === undefined ? {} : { OtherHeadsetId: action.otherHeadsetId }),
      },
      { equipmentId: medinboxId },
    );
  }

  public getHeadsetColorFromConfig(headsetId: number): string {
    const conf = this.medinboxService.medinboxConfiguration();
    const headsetfound = conf?.Headsets.find((h) => h.Id === headsetId);
    return headsetfound?.Color.toLowerCase() ?? 'no-color';
  }

  public getHeadsetDefaultNameFromConfig(headsetId: number): string {
    const color = this.getHeadsetColorFromConfig(headsetId);
    return color.charAt(0).toUpperCase() + color.slice(1);
  }

  public getShortName(name: string): string {
    const splitted = name.trim().split(' ');
    if (splitted.length === 1) {
      const currentName = splitted[0];
      return currentName.length > 1 ? `${currentName[0]}${currentName[1]}`.toUpperCase() : `${currentName[0].toUpperCase()}`;
    } else if (splitted.length === 2) {
      return `${splitted[0][0]}${splitted[1][0]}`.toUpperCase();
    } else if (splitted.length > 2) {
      return `${splitted[0][0]}${splitted[splitted.length - 1][0]}`.toUpperCase();
    }
    return name;
  }

  /** Read from the live state, so a client joining mid-mode sees the whole group at once. */
  exclusiveSpeakerHeadsetIdsObservable(): Observable<number[]> {
    return this.liveService.liveState$.pipe(map((ls) => readExclusiveHeadsetIds(ls)));
  }

  exclusiveSpeakerHeadsetIds(): number[] {
    return readExclusiveHeadsetIds(this.liveService.liveState());
  }

  private exclusiveSpeakerSnapshot(): HeadsetStatus[] {
    const talk = this.liveService.liveState()?.currentStates.find((s) => s.stateType === StateEnum.PRIVATE_TALK);
    const shared = talk?.params?.previousState as HeadsetStatus[] | undefined;
    return shared?.length ? shared : this.previousEquipmentHeadsetStatuses();
  }
}

function readExclusiveHeadsetIds(liveState: LiveState | null | undefined): number[] {
  const ids = liveState?.currentStates.find((s) => s.stateType === StateEnum.PRIVATE_TALK)?.params?.headsetIds;
  return Array.isArray(ids) ? ids.filter((id: unknown): id is number => typeof id === 'number') : [];
}
